/**
 * Cab Module – Search & price at /cabs/search.
 * Uses existing auth (login required for book) and API_BASE for /api/cab.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { FaCar } from "react-icons/fa";

const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "";

export default function CabSearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [cabTypes, setCabTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceLoading, setPriceLoading] = useState(false);
  const [form, setForm] = useState({
    countryId: "",
    cityId: "",
    serviceTypeId: "",
    cabTypeId: "",
    pickupAddress: "",
    dropAddress: "",
    pickupTime: "",
    totalKm: "",
    isPeak: false,
    isNight: false,
  });
  const [priceResult, setPriceResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const [cRes, sRes, tRes] = await Promise.all([
          axios.get(`${API_BASE}/api/cab/user/countries`, { withCredentials: true }),
          axios.get(`${API_BASE}/api/cab/user/services`, { withCredentials: true }),
          axios.get(`${API_BASE}/api/cab/user/cab-types`, { withCredentials: true }),
        ]);
        setCountries(cRes.data?.data || []);
        setServices(sRes.data?.data || []);
        setCabTypes(tRes.data?.data || []);
      } catch (e) {
        setError("Failed to load options");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (!form.countryId) {
      setCities([]);
      return;
    }
    axios
      .get(`${API_BASE}/api/cab/user/cities/${form.countryId}`, { withCredentials: true })
      .then((res) => setCities(res.data?.data || []))
      .catch(() => setCities([]));
  }, [form.countryId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setPriceResult(null);
  };

  const handleGetPrice = async () => {
    if (!form.serviceTypeId || form.totalKm === "") {
      setError("Select service type and enter distance (km).");
      return;
    }
    setError("");
    setPriceLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/cab/user/price`,
        {
          serviceTypeId: form.serviceTypeId,
          cabTypeId: form.cabTypeId || undefined,
          countryId: form.countryId || undefined,
          cityId: form.cityId || undefined,
          totalKm: Number(form.totalKm),
          isPeak: form.isPeak,
          isNight: form.isNight,
        },
        { withCredentials: true }
      );
      setPriceResult(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to get price");
      setPriceResult(null);
    } finally {
      setPriceLoading(false);
    }
  };

  const handleProceedToBook = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    const payload = {
      ...form,
      totalAmount: priceResult?.totalAmount,
      baseFare: priceResult?.baseFare,
      perKmRate: priceResult?.perKmRate,
      peakMultiplier: priceResult?.peakMultiplier,
      nightMultiplier: priceResult?.nightMultiplier,
    };
    navigate("/cabs/booking", { state: payload });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FaCar className="text-blue-600" />
          Cab Search & Price
        </h1>
        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <select
              value={form.countryId}
              onChange={(e) => handleChange("countryId", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select</option>
              {countries.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <select
              value={form.cityId}
              onChange={(e) => handleChange("cityId", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              disabled={!form.countryId}
            >
              <option value="">Select</option>
              {cities.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
            <select
              value={form.serviceTypeId}
              onChange={(e) => handleChange("serviceTypeId", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select</option>
              {services.map((s) => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cab Type</label>
            <select
              value={form.cabTypeId}
              onChange={(e) => handleChange("cabTypeId", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Any</option>
              {cabTypes.map((t) => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address *</label>
            <input
              type="text"
              value={form.pickupAddress}
              onChange={(e) => handleChange("pickupAddress", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g. Airport Terminal 1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Drop Address *</label>
            <input
              type="text"
              value={form.dropAddress}
              onChange={(e) => handleChange("dropAddress", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g. Hotel name, area"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date & Time *</label>
            <input
              type="datetime-local"
              value={form.pickupTime}
              onChange={(e) => handleChange("pickupTime", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km) *</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={form.totalKm}
              onChange={(e) => handleChange("totalKm", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isPeak}
                onChange={(e) => handleChange("isPeak", e.target.checked)}
              />
              <span className="text-sm">Peak hours</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isNight}
                onChange={(e) => handleChange("isNight", e.target.checked)}
              />
              <span className="text-sm">Night</span>
            </label>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button
            type="button"
            onClick={handleGetPrice}
            disabled={priceLoading}
            className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {priceLoading ? "Calculating…" : "Get Price"}
          </button>
          {priceResult && (
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-gray-600">Estimated fare</p>
              <p className="text-2xl font-bold text-gray-900">₹ {priceResult.totalAmount}</p>
              <button
                type="button"
                onClick={handleProceedToBook}
                className="mt-3 w-full py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {user ? "Proceed to Book" : "Login to Book"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
